import { fetchJSON as fetchAPI } from './client';

export type ValueDashboardSnapshot = {
  tenant_id: string; business_value_inr: string; subscription_roi: number;
  success_score: number; success_grade: string; profit_increase_inr: string;
  money_recovered_inr: string; time_saved_hours: number; mistakes_prevented: number;
  improvement_trend: string; generated_at: string;
};

export type ExecutiveSummary = {
  business_health: string; success_score: number; success_grade: string;
  subscription_roi: number; business_value_inr: string;
  platform_utilization_pct: number; recommendation_adoption_pct: number; improvement_trend: string;
};

export type RevenueImpactSnapshot = {
  revenue_influenced_inr: string; revenue_improved_inr: string; revenue_protected_inr: string;
  revenue_recovered_inr: string; opportunity_captured_inr: string; loss_prevented_inr: string;
  period: string; confidence: number;
};

export type ProfitImpactSnapshot = {
  additional_gross_profit_inr: string; additional_net_profit_inr: string;
  margin_improvement_pct: number; profit_protected_inr: string; profit_recovered_inr: string;
  future_opportunity_inr: string; lifetime_profit_inr: string;
};

export type LeakageRecoverySnapshot = {
  marketplace_fees_recovered_inr: string; storage_fees_avoided_inr: string;
  ad_waste_eliminated_inr: string; refund_recovery_inr: string; cash_recovered_inr: string;
  pending_recovery_inr: string; success_rate_pct: number; avg_recovery_days: number;
};

export type TimeSavedSnapshot = {
  manual_hours_eliminated: number; automation_hours: number; reporting_hours: number;
  investigation_hours: number; total_hours_saved: number; equivalent_days: number;
  equivalent_salary_saved_inr: string;
};

export type AutomationImpactSnapshot = {
  tasks_automated: number; manual_tasks_eliminated: number; workflow_executions: number;
  approvals_automated: number; recommendations_auto_executed: number; error_reduction_pct: number;
};

export type RecommendationImpactSnapshot = {
  generated: number; accepted: number; rejected: number; ignored: number;
  success_rate_pct: number; revenue_generated_inr: string; profit_generated_inr: string;
  confidence_improvement_pct: number;
};

export type SuccessScoreSnapshot = {
  overall_score: number; overall_grade: string; adoption_score: number; execution_score: number;
  growth_score: number; financial_score: number; automation_score: number;
  learning_score: number; engagement_score: number;
};

export type SubscriptionROISnapshot = {
  subscription_paid_inr: string; business_value_generated_inr: string; profit_generated_inr: string;
  money_recovered_inr: string; time_saved_hours: number; equivalent_salary_saved_inr: string;
  roi_multiple: number; payback_period_days: number;
};

export type TimelineEntry = {
  period: string; revenue_inr: string; profit_inr: string; roi: number;
  recommendations: number; experiments: number;
};

export type Milestone = {
  id: string; title: string; description: string; achieved_at: string; category: string;
};

export type RenewalCenterSnapshot = {
  value_since_joining_inr: string; roi_multiple: number; profit_inr: string;
  revenue_inr: string; recovery_inr: string; automation_tasks: number; time_saved_hours: number;
  growth_rate_pct: number; recommendations_used: number; platform_usage_pct: number;
  executive_justification: string;
};

export type BenchmarkSnapshot = {
  customer_percentile: number; category_percentile: number; marketplace_percentile: number;
  top_10_pct_gap: string; growth_rate_vs_category: number; improvement_trend: string;
};

export type ExecutiveReport = {
  id: string; type: string; title: string; period: string; status: string; generated_at: string;
};

const COMMAND_BASE = import.meta.env.VITE_COMMANDCENTER_API ?? '/api/v1/command';
const fetchC = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const customerValueApi = {
  dashboard: () => fetchC<ValueDashboardSnapshot>('/customer-value/dashboard'),
  executiveSummary: () => fetchC<ExecutiveSummary>('/customer-value/executive-summary'),
  revenue: () => fetchC<RevenueImpactSnapshot>('/customer-value/revenue'),
  profit: () => fetchC<ProfitImpactSnapshot>('/customer-value/profit'),
  leakage: () => fetchC<LeakageRecoverySnapshot>('/customer-value/leakage'),
  timeSaved: () => fetchC<TimeSavedSnapshot>('/customer-value/time-saved'),
  automation: () => fetchC<AutomationImpactSnapshot>('/customer-value/automation'),
  recommendations: () => fetchC<RecommendationImpactSnapshot>('/customer-value/recommendations'),
  successScore: () => fetchC<SuccessScoreSnapshot>('/customer-value/success-score'),
  roi: () => fetchC<SubscriptionROISnapshot>('/customer-value/roi'),
  timeline: () => fetchC<{ entries: TimelineEntry[] }>('/customer-value/timeline').then((r) => r.entries),
  milestones: () => fetchC<{ milestones: Milestone[] }>('/customer-value/milestones').then((r) => r.milestones),
  renewal: () => fetchC<RenewalCenterSnapshot>('/customer-value/renewal'),
  benchmark: () => fetchC<BenchmarkSnapshot>('/customer-value/benchmark'),
  reports: () => fetchC<{ reports: ExecutiveReport[] }>('/customer-value/reports').then((r) => r.reports),
};
